import React, { Component, Context } from "react";
import { NavLink } from "react-router-dom";
import { HashLink as Link } from "react-router-hash-link";
import { connect } from "react-redux";
import clsx from "clsx";
import AppContext, { IAppContext } from "../../contexts/app/AppContext";
import { IFooterState, mapFooterStateToProps } from "../../redux/reducers/footer";
import { IFooterActionDispatches, mapFooterDispatchToProps } from "../../redux/actions/footer";
import { ISitePage } from "../../laco-common/base/models/page";
import { ILink } from "../../models/cms";
import Logo from "./Logo";
import appstoreIcon from "../../assets/icons/AppStore.png";
import googlePlayIcon from "../../assets/icons/GooglePlay.png";
import "./Footer.scss";

type FooterProps = {
	page: ISitePage;
	bottomOnly?: boolean;
	omitDisclaimer?: boolean;
	className?: string;
};

class Footer extends Component<IFooterState & IFooterActionDispatches & FooterProps> {
	static contextType: Context<IAppContext> = AppContext;

	componentDidMount() {
		this.props.loadFooter();
	}

	renderDonate(classes: string, reverse?: boolean) {
		return (
			<div className={clsx("d-flex flex-column flex-lg-column", classes, reverse && "flex-column-reverse")}>
				<div className={clsx(reverse && "mt-5 mt-0-desktop mb-5-desktop")}>
					<h4>Donate</h4>
					<p>Make a difference today</p>
					<NavLink to={"/donate"} exact className="d-inline-block mb-2">
						<button className={clsx("btn btn-primary", reverse && "w-100")} style={{ minWidth: "148px" }}>
							Donate
						</button>
					</NavLink>
				</div>
				<div className={clsx(!reverse && "mt-5", reverse && "mt-5-tablet")}>
					<h4>Become a Member</h4>
					<p>
						Already a member?{" "}
						<NavLink to={"/login"} exact className="orange-link text-underline">
							Login
						</NavLink>
					</p>
				</div>
			</div>
		);
	}

	renderLinks(links: ILink[]) {
		return (
			<div className="footer-links">
				{(links || []).map((link: ILink, i: number) => {
					if (link.isExternal)
						return (
							<a href={link.path} target="_blank" key={i}>
								{link.title}
							</a>
						);
					return (
						<Link to={`/${link.path}`} exact key={i}>
							{link.title}
						</Link>
					);
				})}
			</div>
		);
	}

	render() {
		const { config } = this.context;
		const { iOSlink, androidLink } = config;

		return (
			<footer className={clsx("container-fluid", this.props.className)}>
				<div className={clsx("container main-footer", this.props.bottomOnly && "bottom-only")}>
					{!this.props.bottomOnly && (
						<div className="row">
							<div className="col-12 col-xl-3 text-center text-xl-left">
								<Logo white width={144} />
								<p className="mt-4-mobile" style={{ marginTop: "5px", marginBottom: "55px" }}>
									{this.props.summary}
								</p>
								<SocialLinks {...this.props} classes="d-none d-md-inline-block mb-4" />

								<h4 className="pb-2-mobile" style={{ marginTop: "12px", marginBottom: "12px" }}>
									Download the Firewire App
								</h4>
								<div className="app-links">
									<a href={iOSlink} target="_blank">
										<img src={appstoreIcon} />
									</a>
									<a href={androidLink} target="_blank">
										<img src={googlePlayIcon} />
									</a>
								</div>
							</div>
							<div className="col-12 col-xl-7">
								<div className="row px-2-mobile px-6-desktop pt-4_5-desktop">
									<div className="col-6 col-md-4 1col-lg-3">
										<h4>Who We Are</h4>
										{this.renderLinks(this.props.linksCol1)}
									</div>
									<div className="col-6 col-md-4 1col-lg-3">
										<h4>Get Support</h4>
										{this.renderLinks(this.props.linksCol2)}
									</div>
									<div className="col-6 col-md-4 1col-lg-3 mt-5-mobile">
										<h4>Community</h4>
										<div className="d-flex1 justify-content-between1">
											{this.renderLinks(this.props.linksCol3)}
										</div>
									</div>
									{this.renderDonate("col-6 col-md-4 1col-lg-3 mt-5-mobile d-block d-xl-none", true)}
								</div>
							</div>
							{this.renderDonate("col-12 col-xl-2 d-none d-xl-block")}
						</div>
					)}
					<div className="copyright d-flex flex-column flex-xl-row align-items-center justify-content-between">
						<span className="bottom-links">
							{!this.props.omitDisclaimer && (
								<NavLink to="/disclaimer" exact>
									Disclaimer
								</NavLink>
							)}
							{process.env.REACT_APP_SHOW_PRIVACY_POLICY && (
								<NavLink to="/privacy" exact>
									Privacy Policy
								</NavLink>
							)}
							<NavLink to="/accessibility" exact>
								Accessibility
							</NavLink>
						</span>
						<span className="copyright-text text-center text-md-left">
							{this.props.copyrightName} © {new Date().getFullYear().toString()}
							&nbsp; |&nbsp; All Rights Reserved
						</span>
					</div>
					<SocialLinks {...this.props} classes="d-inline-block d-xl-none w-100 text-center mt-5" />
				</div>
			</footer>
		);
	}
}

export default connect<IFooterState, IFooterActionDispatches, FooterProps>(
	mapFooterStateToProps,
	mapFooterDispatchToProps
)(Footer);

export function SocialLinks(props) {
	const {
		classes,
		facebookLink,
		instagramLink,
		youtubeLink,
		twitterLink,
		withEmail,
		adminEmail,
		withInstagramTextLink,
	} = props;
	return (
		<>
			<div className={clsx("social-links", classes)}>
				{instagramLink && (
					<a href={instagramLink} target="_blank">
						<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
							<g clip-path="url(#clip0_439_4850)">
								<path
									d="M12 2.16094C15.2063 2.16094 15.5859 2.175 16.8469 2.23125C18.0188 2.28281 18.6516 2.47969 19.0734 2.64375C19.6313 2.85938 20.0344 3.12188 20.4516 3.53906C20.8734 3.96094 21.1313 4.35938 21.3469 4.91719C21.5109 5.33906 21.7078 5.97656 21.7594 7.14375C21.8156 8.40937 21.8297 8.78906 21.8297 11.9906C21.8297 15.1969 21.8156 15.5766 21.7594 16.8375C21.7078 18.0094 21.5109 18.6422 21.3469 19.0641C21.1313 19.6219 20.8687 20.025 20.4516 20.4422C20.0297 20.8641 19.6313 21.1219 19.0734 21.3375C18.6516 21.5016 18.0141 21.6984 16.8469 21.75C15.5813 21.8062 15.2016 21.8203 12 21.8203C8.79375 21.8203 8.41406 21.8062 7.15313 21.75C5.98125 21.6984 5.34844 21.5016 4.92656 21.3375C4.36875 21.1219 3.96563 20.8594 3.54844 20.4422C3.12656 20.0203 2.86875 19.6219 2.65313 19.0641C2.48906 18.6422 2.29219 18.0047 2.24063 16.8375C2.18438 15.5719 2.17031 15.1922 2.17031 11.9906C2.17031 8.78438 2.18438 8.40469 2.24063 7.14375C2.29219 5.97187 2.48906 5.33906 2.65313 4.91719C2.86875 4.35938 3.13125 3.95625 3.54844 3.53906C3.97031 3.11719 4.36875 2.85938 4.92656 2.64375C5.34844 2.47969 5.98594 2.28281 7.15313 2.23125C8.41406 2.175 8.79375 2.16094 12 2.16094ZM12 0C8.74219 0 8.33438 0.0140625 7.05469 0.0703125C5.77969 0.126563 4.90313 0.332812 4.14375 0.628125C3.35156 0.9375 2.68125 1.34531 2.01563 2.01562C1.34531 2.68125 0.9375 3.35156 0.628125 4.13906C0.332812 4.90313 0.126563 5.775 0.0703125 7.05C0.0140625 8.33437 0 8.74219 0 12C0 15.2578 0.0140625 15.6656 0.0703125 16.9453C0.126563 18.2203 0.332812 19.0969 0.628125 19.8563C0.9375 20.6484 1.34531 21.3188 2.01563 21.9844C2.68125 22.65 3.35156 23.0625 4.13906 23.3672C4.90313 23.6625 5.775 23.8687 7.05 23.925C8.32969 23.9812 8.7375 23.9953 11.9953 23.9953C15.2531 23.9953 15.6609 23.9812 16.9406 23.925C18.2156 23.8687 19.0922 23.6625 19.8516 23.3672C20.6391 23.0625 21.3094 22.65 21.975 21.9844C22.6406 21.3188 23.0531 20.6484 23.3578 19.8609C23.6531 19.0969 23.8594 18.225 23.9156 16.95C23.9719 15.6703 23.9859 15.2625 23.9859 12.0047C23.9859 8.74688 23.9719 8.33906 23.9156 7.05938C23.8594 5.78438 23.6531 4.90781 23.3578 4.14844C23.0625 3.35156 22.6547 2.68125 21.9844 2.01562C21.3188 1.35 20.6484 0.9375 19.8609 0.632812C19.0969 0.3375 18.225 0.13125 16.95 0.075C15.6656 0.0140625 15.2578 0 12 0Z"
									fill="#EB7635"
								/>
								<path
									d="M12 5.83594C8.59688 5.83594 5.83594 8.59688 5.83594 12C5.83594 15.4031 8.59688 18.1641 12 18.1641C15.4031 18.1641 18.1641 15.4031 18.1641 12C18.1641 8.59688 15.4031 5.83594 12 5.83594ZM12 15.9984C9.79219 15.9984 8.00156 14.2078 8.00156 12C8.00156 9.79219 9.79219 8.00156 12 8.00156C14.2078 8.00156 15.9984 9.79219 15.9984 12C15.9984 14.2078 14.2078 15.9984 12 15.9984Z"
									fill="#EB7635"
								/>
								<path
									d="M19.8469 5.59141C19.8469 6.38828 19.2 7.03047 18.4078 7.03047C17.6109 7.03047 16.9688 6.3836 16.9688 5.59141C16.9688 4.79453 17.6156 4.15234 18.4078 4.15234C19.2 4.15234 19.8469 4.79922 19.8469 5.59141Z"
									fill="#EB7635"
								/>
							</g>
							<defs>
								<clipPath id="clip0_439_4850">
									<rect width="24" height="24" fill="white" />
								</clipPath>
							</defs>
						</svg>
					</a>
				)}
				{withEmail && (
					<a href={`mailto:${adminEmail}`} target="_blank">
						<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 18" fill="none">
							<path
								d="M0.00500015 2.82533L11.9999 8.82279L23.995 2.82526C23.9045 1.24973 22.5982 0 21 0H3C1.40178 0 0.0954521 1.24977 0.00500015 2.82533Z"
								fill="#EB7635"
							/>
							<path
								d="M24 6.17686L11.9999 12.1769L0 6.17693V15C0 16.6569 1.34315 18 3 18H21C22.6569 18 24 16.6569 24 15V6.17686Z"
								fill="#EB7635"
							/>
						</svg>
					</a>
				)}
				{facebookLink && (
					<a href={facebookLink} target="_blank">
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							style={{ marginTop: "-1px" }}
						>
							<g clip-path="url(#clip0_439_4848)">
								<path
									d="M24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 17.9895 4.3882 22.954 10.125 23.8542V15.4688H7.07812V12H10.125V9.35625C10.125 6.34875 11.9166 4.6875 14.6576 4.6875C15.9701 4.6875 17.3438 4.92188 17.3438 4.92188V7.875H15.8306C14.34 7.875 13.875 8.80008 13.875 9.75V12H17.2031L16.6711 15.4688H13.875V23.8542C19.6118 22.954 24 17.9895 24 12Z"
									fill="#EB7635"
								/>
							</g>
							<defs>
								<clipPath id="clip0_439_4848">
									<rect width="24" height="24" fill="white" />
								</clipPath>
							</defs>
						</svg>
					</a>
				)}
				{youtubeLink && (
					<a href={youtubeLink} target="_blank">
						<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
							<path d="M4.652 0h1.44l.988 3.702.916-3.702h1.454l-1.665 5.505v3.757h-1.431v-3.757l-1.702-5.505zm6.594 2.373c-1.119 0-1.861.74-1.861 1.835v3.349c0 1.204.629 1.831 1.861 1.831 1.022 0 1.826-.683 1.826-1.831v-3.349c0-1.069-.797-1.835-1.826-1.835zm.531 5.127c0 .372-.19.646-.532.646-.351 0-.554-.287-.554-.646v-3.179c0-.374.172-.651.529-.651.39 0 .557.269.557.651v3.179zm4.729-5.07v5.186c-.155.194-.5.512-.747.512-.271 0-.338-.186-.338-.46v-5.238h-1.27v5.71c0 .675.206 1.22.887 1.22.384 0 .918-.2 1.468-.853v.754h1.27v-6.831h-1.27zm2.203 13.858c-.448 0-.541.315-.541.763v.659h1.069v-.66c.001-.44-.092-.762-.528-.762zm-4.703.04c-.084.043-.167.109-.25.198v4.055c.099.106.194.182.287.229.197.1.485.107.619-.067.07-.092.105-.241.105-.449v-3.359c0-.22-.043-.386-.129-.5-.147-.193-.42-.214-.632-.107zm4.827-5.195c-2.604-.177-11.066-.177-13.666 0-2.814.192-3.146 1.892-3.167 6.367.021 4.467.35 6.175 3.167 6.367 2.6.177 11.062.177 13.666 0 2.814-.192 3.146-1.893 3.167-6.367-.021-4.467-.35-6.175-3.167-6.367zm-12.324 10.686h-1.363v-7.54h-1.41v-1.28h4.182v1.28h-1.41v7.54zm4.846 0h-1.21v-.718c-.223.265-.455.467-.696.605-.652.374-1.547.365-1.547-.955v-5.438h1.209v4.988c0 .262.063.438.322.438.236 0 .564-.303.711-.487v-4.939h1.21v6.506zm4.657-1.348c0 .805-.301 1.431-1.106 1.431-.443 0-.812-.162-1.149-.583v.5h-1.221v-8.82h1.221v2.84c.273-.333.644-.608 1.076-.608.886 0 1.18.749 1.18 1.631v3.609zm4.471-1.752h-2.314v1.228c0 .488.042.91.528.91.511 0 .541-.344.541-.91v-.452h1.245v.489c0 1.253-.538 2.013-1.813 2.013-1.155 0-1.746-.842-1.746-2.013v-2.921c0-1.129.746-1.914 1.837-1.914 1.161 0 1.721.738 1.721 1.914v1.656z" />
						</svg>
					</a>
				)}
				{twitterLink && (
					<a href={twitterLink} target="_blank">
						<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
							<path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
						</svg>
					</a>
				)}
			</div>
			{withInstagramTextLink && (
				<a
					href={instagramLink}
					target="_blank"
					className="d-none d-md-flex flex-row align-items-center mt-3 mb-5 text-md text-uppercase font-weight-bold orange-link"
				>
					View more on Instagram{" "}
					<svg
						className="ml-2"
						xmlns="http://www.w3.org/2000/svg"
						width="16"
						height="14"
						viewBox="0 0 16 14"
						fill="none"
					>
						<path
							d="M9.14815 1.81445L14.3333 6.99964M14.3333 6.99964L9.14815 12.1848M14.3333 6.99964L1 6.99964"
							stroke-width="1.77778"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</svg>
				</a>
			)}
		</>
	);
}
